export type MediaCompressionType = 'video' | 'image';

export interface CompressionLogEntry {
  type: MediaCompressionType;
  originalName: string;
  originalSize: number;
  compressedSize: number;
  reductionPercent: number;
  timestamp: string;
}

const compressionStats: Record<MediaCompressionType, CompressionLogEntry[]> = {
  video: [],
  image: []
};

let ffmpegPromise: Promise<import('@ffmpeg/ffmpeg').FFmpeg> | null = null;
let videoCompressionQueue = Promise.resolve();
let compressionId = 0;

const toMb = (bytes: number) => (bytes / (1024 * 1024));

const calculateReduction = (originalSize: number, compressedSize: number): number => {
  if (!originalSize) return 0;
  return Math.max(0, ((originalSize - compressedSize) / originalSize) * 100);
};

const logCompression = (entry: CompressionLogEntry) => {
  const existing = compressionStats[entry.type];
  if (existing.length < 20) {
    existing.push(entry);
  } else {
    existing.shift();
    existing.push(entry);
  }

  console.info('[Clue200 compression]', {
    type: entry.type,
    originalName: entry.originalName,
    originalSizeBytes: entry.originalSize,
    compressedSizeBytes: entry.compressedSize,
    reductionPercent: Number(entry.reductionPercent.toFixed(1)),
    originalSizeMb: Number(toMb(entry.originalSize).toFixed(2)),
    compressedSizeMb: Number(toMb(entry.compressedSize).toFixed(2)),
    timestamp: entry.timestamp
  });
};

const sanitizeVideoOutput = async (file: File): Promise<File> => {
  const [{ FFmpeg }, { fetchFile, toBlobURL }] = await Promise.all([
    import('@ffmpeg/ffmpeg'),
    import('@ffmpeg/util')
  ]);
  const ext = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'mp4';
  const id = `${Date.now()}-${++compressionId}`;
  const inputName = `input-${id}.${ext}`;
  const outputNames = [1, 2, 3].map(attempt => `compressed-${id}-${attempt}.mp4`);
  const getFFmpeg = async (): Promise<import('@ffmpeg/ffmpeg').FFmpeg> => {
    if (!ffmpegPromise) {
      ffmpegPromise = (async () => {
        const ffmpeg = new FFmpeg();
        const cdnBases = [
          'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/esm',
          'https://unpkg.com/@ffmpeg/core@0.12.10/dist/esm'
        ];
        let lastError: unknown;

        for (const baseUrl of cdnBases) {
          try {
            await ffmpeg.load({
              coreURL: await toBlobURL(`${baseUrl}/ffmpeg-core.js`, 'text/javascript'),
              wasmURL: await toBlobURL(`${baseUrl}/ffmpeg-core.wasm`, 'application/wasm')
            });
            return ffmpeg;
          } catch (error) {
            lastError = error;
          }
        }

        throw lastError instanceof Error
          ? lastError
          : new Error('Unable to load the video compression engine.');
      })().catch(error => {
        ffmpegPromise = null;
        throw error;
      });
    }
    return ffmpegPromise;
  };

  const run = async (): Promise<File> => {
    const ffmpeg = await getFFmpeg();
    await ffmpeg.writeFile(inputName, await fetchFile(file));
    let bestCandidate: File | null = null;

    try {
      for (const [index, outputName] of outputNames.entries()) {
        await ffmpeg.exec([
          '-i', inputName,
          '-vf', 'scale=min(1920\\,iw):min(1920\\,ih):force_original_aspect_ratio=decrease:force_divisible_by=2',
          '-c:v', 'libx264',
          '-preset', 'veryfast',
          '-crf', String([28, 32, 36][index]),
          '-pix_fmt', 'yuv420p',
          '-c:a', 'aac',
          '-b:a', '96k',
          '-movflags', '+faststart',
          '-maxrate', '1.5M',
          '-bufsize', '3M',
          outputName
        ]);
        const data = await ffmpeg.readFile(outputName);
        if (typeof data === 'string') {
          throw new Error('The video compression engine returned an invalid output.');
        }
        const bytes = new Uint8Array(data.byteLength);
        bytes.set(data);
        const candidate = new File([bytes.buffer], outputName, { type: 'video/mp4' });
        if (!bestCandidate || candidate.size < bestCandidate.size) bestCandidate = candidate;
        await ffmpeg.deleteFile(outputName);
        if (candidate.size < file.size) break;
      }
    } finally {
      await ffmpeg.deleteFile(inputName);
      for (const outputName of outputNames) {
        try {
          await ffmpeg.deleteFile(outputName);
        } catch {
          // A failed encode may not have created this output file.
        }
      }
    }

    if (!bestCandidate) throw new Error('The selected video could not be compressed.');
    return bestCandidate.size < file.size ? bestCandidate : file;
  };

  const queuedRun = videoCompressionQueue.then(run, run);
  videoCompressionQueue = queuedRun.then(() => undefined, () => undefined);
  return queuedRun;
};

const compressImageFile = async (file: File): Promise<File> => {
  const { default: imageCompression } = await import('browser-image-compression');
  try {
    const candidate = await imageCompression(file, {
      maxSizeMB: 1.0,
      maxWidthOrHeight: 1080,
      initialQuality: 0.8,
      useWebWorker: true,
      fileType: 'image/webp',
      alwaysKeepResolution: false
    });
    return candidate.size < file.size ? candidate : candidate;
  } catch (error) {
    console.warn('[Compression] Fallback to original image:', error);
    return file;
  }
};

export interface CompressedImagePair {
  fullFile: File;
  thumbFile: File;
}

export const compressImageForUpload = async (file: File): Promise<CompressedImagePair> => {
  const { default: imageCompression } = await import('browser-image-compression');
  let fullFile: File = file;
  let thumbFile: File = file;

  try {
    fullFile = await imageCompression(file, {
      maxSizeMB: 1.0,
      maxWidthOrHeight: 1080,
      initialQuality: 0.8,
      useWebWorker: true,
      fileType: 'image/webp'
    });
  } catch (e) {
    console.warn('[Compression] Failed full image compression:', e);
  }

  try {
    thumbFile = await imageCompression(file, {
      maxSizeMB: 0.3,
      maxWidthOrHeight: 480,
      initialQuality: 0.7,
      useWebWorker: true,
      fileType: 'image/webp'
    });
  } catch (e) {
    console.warn('[Compression] Failed thumbnail image compression:', e);
    thumbFile = fullFile;
  }

  return { fullFile, thumbFile };
};

const compressVideoFile = async (file: File): Promise<File> => {
  return sanitizeVideoOutput(file);
};

export const compressMedia = async (
  file: File,
  type: MediaCompressionType,
  onProgress?: (progress: number, stage: 'compressing' | 'finalizing') => void
): Promise<File> => {
  if (!file || !file.size) {
    return file;
  }

  const originalSize = file.size;
  const originalName = file.name;

  onProgress?.(10, 'compressing');
  const compressedFile = type === 'image'
    ? await compressImageFile(file)
    : await compressVideoFile(file);

  onProgress?.(90, 'compressing');
  const reduction = calculateReduction(originalSize, compressedFile.size);
  const entry: CompressionLogEntry = {
    type,
    originalName,
    originalSize,
    compressedSize: compressedFile.size,
    reductionPercent: reduction,
    timestamp: new Date().toISOString()
  };

  logCompression(entry);
  onProgress?.(100, 'finalizing');
  return compressedFile.size > 0 ? compressedFile : file;
};

export const getCompressionStats = () => ({
  video: [...compressionStats.video],
  image: [...compressionStats.image]
});
